import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserService } from './user/user.service';
import { ApplicationConfigService } from './application-config.service';
import { Profile } from '../api/authentication-service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  userService : UserService = inject(UserService);
  appConfig: ApplicationConfigService = inject(ApplicationConfigService);


  constructor(private http: HttpClient) {}

  /**
   * Checks whether the user is authenticated. Backend decides this based on
   * the HttpOnly access_token cookie set by the BFF (server.js).
   */
  checkAuthentication(): Promise<boolean> {
    return new Promise(resolve => {
      this.http.get('/auth/me', { withCredentials: true })
        .subscribe({
          next: () => resolve(true),
          error: () => resolve(false)
        });
    });
  }

  /**
   * Redirects to the BFF /oauth2/authenticate which builds the authorize URL
   * server-side and redirects the browser to the OAuth provider. After the
   * provider returns to /oauth2/callback, the BFF finishes the code/token
   * exchange and sets HttpOnly cookies before bouncing back to the SPA.
   */
  authenticate(): void {
    window.location.assign('/oauth2/authenticate');
  }

  /**
   * Calls the BFF logout, which clears server-side state. Cookies are wiped
   * by the proxy onProxyRes (regardless of upstream status) so the browser
   * is always in a clean state.
   */
  logout(): Promise<void> {
    return new Promise(resolve => {
      this.http.post('/oauth2/logout', {}, { withCredentials: true })
        .subscribe({
          next: () => resolve(),
          error: () => resolve()
        });
    });
  }

  /**
   * Pulls userId + authorities from /oauth2/me. The Angular layer never
   * decodes the JWT (it can't — the access_token is HttpOnly), so the
   * authentication-service is the source of truth for the current profile.
   */
  loadProfile(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService.loadProfile(
          {
            onStart: () => {},
            onSuccess: (profile : Profile) => {
              this.appConfig.set(profile);
              resolve();
            },
            onError: (error: string) => {
              // Don't redirect to /oauth2/authenticate here. The cookies are
              // valid (checkAuthentication() returned true above) — re-auth
              // would put us right back into the same /me call and loop.
              reject(error);
            },
            onComplete: () => {}
          }
      );
    });
  }

  loadSessionId(): Promise<string | null> {
    const existing = localStorage.getItem('uiSessionId');
    if (existing) {
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      this.http.get('/session', {
        withCredentials: true,
        responseType: 'text'
      }).subscribe({
        next: sessionId => {
          if (sessionId) {
            localStorage.setItem('uiSessionId', sessionId);
            resolve(sessionId);
          } else {
            resolve(null);
          }
        },
        error: err => {
          console.error('Failed to load UI session id', err);
          resolve(null);
        }
      });
    });
  }

}
